// src/pages/RootError.tsx
import { type ErrorComponentProps } from "@tanstack/react-router";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { useTranslate } from "@/hooks/use-translate";

export default function RootError({ error }: ErrorComponentProps) {
  const { t } = useTranslate();

  let title = t("Oops! Something went wrong");
  let message = t("Please refresh the page or try again later. ");

  // Customize friendly message based on status code
    if ("status" in error) {
      const status = (error as any).status;

      if (status === 404)
        message = t("The page you requested could not be found.");
      else if (status === 401 || status === 403)
        message = t("You do not have permission to access this page.");
      else message = t("Something went wrong. Please try again later.");
    } else {
      // For other JS errors, always show a generic friendly message
      message = t("Something went wrong. Please try again later.");
    }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Left side illustration */}
      <div className="w-1/2 hidden md:block relative overflow-hidden border-r border-gray-100">
        <div className="absolute top-8 left-8">
          <img src="/assets/images/logo.svg" alt={t("Logo")} width={140} />
        </div>
        <div className="flex items-center justify-center h-screen">
          <img
            src="/assets/images/error-illustration.svg"
            alt={t("Error illustration")}
            className="w-2/3"
          />
        </div>
      </div>

      {/* Right side content */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 bg-white relative">
        <div className="w-full max-w-md text-center">
          {/* Icon visual */}
          <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>

          <h1 className="text-2xl text-gray-900 mb-3">{title}</h1>
          <p className="text-gray-600 mb-8 leading-relaxed">{message}</p>

          <Alert
            name="error_fallback"
            message={t("If the problem persists, please contact support.")}
            className="mb-6"
          />

          <div className="space-y-4">
            {/* Button to refresh the page */}
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              {t("Refresh")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
